import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import SearchComponent from "../../components/SearchComponent";
import TableComponent from "../../components/TableComponent";
import TagComponent from "../../components/TagComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import ExcelButtonComponent from "../../components/ExcelButtonComponent";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	ActionButtonOverlay,
	FiltersContainer
} from "../../components/styled/common";
import HintComponent from "../../components/HintComponent";
import { useEventApplicationContext } from "../../context/EventApplicationContext/EventApplicationContext";
import { eventStateColors } from "../../context/EventContext/constants";

const EventApplicationList: React.FC = () => {
	const navigate = useNavigate();
	const {
		applicationList,
		clearApplicationList,
		getEventApplicationList,
		exportEventApplicationsExcel,
		operationStatus,
	} = useEventApplicationContext();

	const [searchText, setSearchText] = useState("");

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return applicationList.filter((p) => {
			const fullName = `${p.eventName ?? ""}`.toLocaleLowerCase("tr-TR");
			const nameMatch = !search || fullName.includes(search);
			return nameMatch;
		});
	}, [applicationList, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isExporting = operationStatus.type === "loading" && operationStatus.operation === "export";

	const handleExport = () => {
		exportEventApplicationsExcel();
	};

	useEffect(() => {
		clearApplicationList();
		getEventApplicationList();

		return () => {
			clearApplicationList();
		};
	}, []);

	const columns = useMemo(() => [
		createColumn({
			title: "Proje Başlığı",
			dataIndex: "eventName",
			width: 90,
			sorterType: "string",
			render: (text, record) => (
				<button
					type="button"
					onClick={() => navigate(`/event-applications/${record.eventId}/participants`)}
					style={{ cursor: "pointer", color: "#1890ff", background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left" }}
				>
					{record.eventName}
				</button>
			),
		}),
		createColumn({
			title: "Başvuru Sayısı",
			dataIndex: "applyCount",
			width: 20,
			sorterType: "numeric"
		}),
		createColumn({
			title: "Son Başvuru Tarihi",
			dataIndex: "projectDate",
			width: 30,
			sorterType: "string",
			render: (text, record) => (record.projectDate ? tableDateFormatter(record.projectDate, "DD.MM.YYYY") : "-")
		}),
		createColumn({
			title: "Başvuru Durumu",
			dataIndex: "applicationStatus",
			width: 30,
			render: (text, record) => {
				const status = record.applicationStatus;

				if (!status) return "-";

				const color = eventStateColors[status.stateCode] ?? "grey";

				return (
					<TagComponent label={status.header || "-"} color={color} />
				);
			}
		}),
		createColumn({
			title: "Eklenme Tarihi",
			dataIndex: "createdDate",
			width: 30,
			sorterType: "string",
			render: (text, record) => (record.createdDate ? tableDateFormatter(record.createdDate, "DD.MM.YYYY") : "-")
		})
	], []);

	return (
		<div>
			<HeaderComponent>{"Proje Başvuru Özetleri"}</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<HintComponent text="Projeye ait başvuranları görüntülemek için proje başlığına tıklayabilirsiniz." />
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Proje Başlığı Ara"
								style={{ width: "12vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<ExcelButtonComponent
								loading={isExporting}
								disabled={applicationList.length === 0}
								onClick={handleExport}
							/>
						</ActionButtonOverlay>
					</ContainerHeader>
					<TableComponent
						columns={columns}
						dataSource={tableData as any[]}
					/>
				</Spin>
			</Container>
		</div>
	);
};

export default EventApplicationList;